import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentWrapper, useToast } from '@textile-erp/ui';
import { createApiClient, buildBackendUrl } from '@textile-erp/core';
import {
  ArrowLeft,
  User as UserIcon,
  Shield,
  Activity,
  LogOut,
  Mail,
  Phone,
  Clock,
  Briefcase,
  Building2,
  Calendar,
  BadgeCheck,
  UserCircle,
  KeyRound,
  ScrollText,
} from 'lucide-react';
import { useAuthContext } from '../providers/AuthProvider';
import { ChangeOwnPasswordModal } from '../components/ChangeOwnPasswordModal';

const api = createApiClient(buildBackendUrl('/api'));

type TabId = 'profile' | 'security' | 'activity';

interface AuditLog {
  id: string;
  action: string;
  resource: string;
  platform?: string | null;
  ipAddress?: string | null;
  createdAt: string;
  details?: any;
}

const TABS: { id: TabId; label: string; icon: typeof UserIcon }[] = [
  { id: 'profile', label: 'My Profile', icon: UserIcon },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'activity', label: 'Activity Log', icon: Activity },
];

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString();
}

function formatDate(iso?: string | Date | null): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  }
  return (email?.[0] || 'U').toUpperCase();
}

const ACTION_COLORS: Record<string, string> = {
  login: 'bg-emerald-500',
  login_failure: 'bg-red-500',
  logout: 'bg-gray-400',
  session_expired: 'bg-amber-500',
  token_refresh: 'bg-blue-500',
};

export function ProfileSettingsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const { showError } = useToast();

  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [freshUser, setFreshUser] = useState<any>(null);
  const [employeeProfile, setEmployeeProfile] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/auth/me')
      .then((res: any) => {
        if (!cancelled) setFreshUser(res);
      })
      .catch(() => {});
    api
      .get('/purchase/employees/my-profile')
      .then((res: any) => {
        if (!cancelled) setEmployeeProfile(res?.data || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const u = (freshUser || user) as any;
  const displayName = u?.name || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.email || 'User';
  const role = u?.roles?.[0]?.name || 'User';

  useEffect(() => {
    if (activeTab !== 'activity' || !u?.id) return;

    let cancelled = false;
    setLogsLoading(true);
    api
      .get(`/monitoring/audit-logs/auth?userId=${u.id}&limit=10`)
      .then((res: any) => {
        if (!cancelled) setLogs(res?.data || []);
      })
      .catch(() => {
        if (!cancelled) setLogs([]);
      })
      .finally(() => {
        if (!cancelled) setLogsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, u?.id]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e: any) {
      showError('Logout failed', e?.message || 'Please try again');
    }
  };

  const breadcrumbs = useMemo(
    () => [
      { label: 'Home', href: '/' },
      { label: 'Profile Settings' },
    ],
    [],
  );

  return (
    <ContentWrapper breadcrumbs={breadcrumbs}>
      <div className="w-full px-4 py-4">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Profile Settings</h1>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        </div>

        <div className="grid grid-cols-12 gap-5 min-h-[calc(100vh-140px)]">
          <aside className="col-span-12 md:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden h-full flex flex-col">
              <div className="p-6 text-center bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white">
                <div className="relative w-20 h-20 mx-auto">
                  {u?.avatar ? (
                    <img
                      src={u.avatar}
                      alt={displayName}
                      className="w-20 h-20 rounded-full object-cover ring-4 ring-white/30"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm ring-4 ring-white/30 flex items-center justify-center text-white text-2xl font-bold uppercase">
                      {getInitials(u?.name, u?.email)}
                    </div>
                  )}
                </div>
                <div className="mt-3 font-semibold text-white">{displayName}</div>
                <div className="text-xs text-blue-100 break-all">{u?.email}</div>
                <span className="inline-block mt-2 px-2.5 py-0.5 text-[10px] font-medium rounded-full bg-white/20 text-white border border-white/30 backdrop-blur-sm">
                  {role}
                </span>
              </div>

              <nav className="p-2 flex flex-col flex-1">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  const active = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setActiveTab(t.id)}
                      className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md border-l-2 transition-colors ${
                        active
                          ? 'text-blue-600 border-blue-600 bg-blue-50 dark:bg-blue-900/20 font-medium'
                          : 'text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {t.label}
                    </button>
                  );
                })}
                <div className="flex-1" />
                <hr className="my-2 border-gray-200 dark:border-gray-700" />
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </nav>

              {u?.lastLoginAt && (
                <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Last Login</div>
                      <div className="text-xs font-medium text-gray-900 dark:text-white truncate">
                        {formatDate(u.lastLoginAt)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>

          <main className="col-span-12 md:col-span-9 space-y-5">
            {activeTab === 'profile' && (
              <>
                <Section
                  title="Personal Information"
                  icon={<UserCircle className="w-5 h-5" />}
                  iconColor="blue"
                >
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <Field
                      label="Full Name"
                      icon={<UserIcon className="w-4 h-4" />}
                      value={displayName}
                    />
                    <Field
                      label="Email Address"
                      icon={<Mail className="w-4 h-4" />}
                      value={u?.email}
                    />
                    <Field
                      label="Mobile Number"
                      icon={<Phone className="w-4 h-4" />}
                      value={u?.phone || '—'}
                    />
                    <Field
                      label="Role"
                      icon={<BadgeCheck className="w-4 h-4" />}
                      value={role}
                    />
                    <Field
                      label="Joined On"
                      icon={<Calendar className="w-4 h-4" />}
                      value={formatDate(u?.createdAt)}
                    />
                    <Field
                      label="Status"
                      value={
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {u?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      }
                    />
                  </div>
                </Section>

                {employeeProfile && (
                  <Section
                    title="Employee Details"
                    description="Mapped employee record from Purchase Management"
                    icon={<Briefcase className="w-5 h-5" />}
                    iconColor="purple"
                  >
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      <Field
                        label="Employee Code"
                        value={employeeProfile.employeeCode || '—'}
                      />
                      <Field
                        label="Designation"
                        icon={<Briefcase className="w-4 h-4" />}
                        value={employeeProfile.designation?.name || '—'}
                      />
                      <div className="md:col-span-2">
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 uppercase tracking-wide">Department(s)</div>
                        {employeeProfile.departments?.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {employeeProfile.departments.map((d: any) => (
                              <span
                                key={d.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                              >
                                <Building2 className="w-3 h-3" />
                                {d.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">—</span>
                        )}
                      </div>
                    </div>
                  </Section>
                )}
              </>
            )}

            {activeTab === 'security' && (
              <Section
                title="Security"
                description="Manage password and login security"
                icon={<KeyRound className="w-5 h-5" />}
                iconColor="amber"
              >
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  <Row
                    title="Password"
                    subtitle="Change using your current password"
                    action={
                      <button
                        type="button"
                        onClick={() => setShowChangePassword(true)}
                        className="text-xs px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Change Password
                      </button>
                    }
                  />
                  <Row
                    title="Staff Code Login"
                    subtitle="Set by an admin in User Management — shown here for reference"
                    action={
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border ${
                          u?.isStaffLogin
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-gray-50 text-gray-600 border-gray-200'
                        }`}
                      >
                        {u?.isStaffLogin ? 'Enabled' : 'Disabled'}
                      </span>
                    }
                  />
                  <Row
                    title="Email Verified"
                    subtitle="Verification status of your email"
                    action={
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border ${
                          u?.emailVerified
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}
                      >
                        {u?.emailVerified ? 'Verified' : 'Pending'}
                      </span>
                    }
                  />
                </div>
              </Section>
            )}

            {activeTab === 'activity' && (
              <Section
                title="Activity Log"
                description="Last 10 login events"
                icon={<ScrollText className="w-5 h-5" />}
                iconColor="emerald"
                headerRight={
                  <button
                    type="button"
                    onClick={() => navigate('/purchase/auth-logs')}
                    className="text-xs px-3 py-1.5 rounded-md border border-blue-200 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium"
                  >
                    See Full History
                  </button>
                }
              >
                {logsLoading ? (
                  <div className="px-6 py-10 text-center text-sm text-gray-500">Loading...</div>
                ) : logs.length === 0 ? (
                  <div className="px-6 py-10 text-center text-sm text-gray-500">No activity yet</div>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                      {logs.slice(0, 10).map((log) => (
                        <li
                          key={log.id}
                          className="px-6 py-3 flex items-center gap-3 text-sm text-gray-700 dark:text-gray-200"
                        >
                          <span
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${ACTION_COLORS[log.action] || 'bg-gray-400'}`}
                          />
                          <span>{log.action.replace(/_/g, ' ')}</span>
                          {log.platform && (
                            <span className="text-xs text-gray-400">· {log.platform}</span>
                          )}
                          {log.ipAddress && (
                            <span className="text-xs text-gray-400">· {log.ipAddress}</span>
                          )}
                          <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
                            {formatRelativeTime(log.createdAt)}
                          </span>
                        </li>
                      ))}
                    </ul>
                )}
              </Section>
            )}

          </main>
        </div>
      </div>

      <ChangeOwnPasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        email={u?.email || ''}
      />
    </ContentWrapper>
  );
}

function Section({
  title,
  description,
  headerRight,
  icon,
  iconColor = 'blue',
  children,
}: {
  title: string;
  description?: string;
  headerRight?: React.ReactNode;
  icon?: React.ReactNode;
  iconColor?: 'blue' | 'emerald' | 'purple' | 'amber' | 'rose';
  children: React.ReactNode;
}) {
  const iconColors: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-100',
  };
  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
      <header className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {icon && (
            <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${iconColors[iconColor]}`}>
              {icon}
            </div>
          )}
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">{title}</h2>
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
            )}
          </div>
        </div>
        {headerRight}
      </header>
      {children}
    </section>
  );
}

function Field({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 mb-1 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
        {icon && <span className="text-gray-400 flex-shrink-0">{icon}</span>}
        <span className="flex items-center gap-1.5 flex-wrap">{value}</span>
      </div>
    </div>
  );
}

function Row({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="px-6 py-4 flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-gray-900 dark:text-white">{title}</div>
        {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}


export default ProfileSettingsPage;
